import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
// @mui
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Link,
} from '@mui/material';
import { toast } from 'react-toastify';
import {
  Button,
  ModalAddOvertimeShift,
  ModalComponent,
  ModalEditOvertimeShift,
  ConfirmDeleteModal,
  Pagination,
  LoadingComponent,
} from '../../components';
import { overtimeShiftList, deleteOvertimeShift } from '../../API';
import { useHead } from 'src/context/HeadContext';

const DAY_LABEL = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];

export default function OvertimeShift() {
  const [id, setId] = useState('');
  const [isShowAddModal, setIsShowModal] = useState(false);
  const [isEditModalShow, setIsEditModalShow] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [data, setData] = useState([]);
  const size = 10;
  const [isLoading, setIsLoading] = useState(false);

  const handleEditModal = (id) => {
    setId(id);
    setIsEditModalShow(true);
  };

  const getList = async (page) => {
    try {
      setIsLoading(true);
      const response = await overtimeShiftList({ size, page });
      if (response.meta.code === 200) {
        setData(response?.data || []);
        setTotalPages(response?.page_info?.last_page || 1);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getList(currentPage);
  }, [currentPage]);

  const handleChildData = () => {
    setCurrentPage(1);
    getList(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleDelete = async (id) => {
    try {
      const res = await deleteOvertimeShift(id);
      if (res.meta.code === 200) {
        toast.success('Shift lembur berhasil dihapus');
        setDeleteTarget(null);
        getList(currentPage);
      }
    } catch (error) {
      const message = error.response?.data?.meta?.message || 'Shift lembur gagal dihapus';
      toast.error(message);
      throw error;
    }
  };

  const formatTime = (value) => (value ? String(value).slice(0, 5) : '-');

  const { helmet, favicon } = useHead();

  return (
    <>
      <Helmet>
        <title>{helmet.title}</title>
        <link rel="icon" type="image/png" sizes="32x32" href={favicon} />
        <link rel="icon" type="image/png" sizes="16x16" href={favicon} />
      </Helmet>

      <Box sx={{ overflow: 'hidden', px: 4, py: 3 }}>
        <Grid container>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <Grid container spacing={2} sx={{ mb: 4 }} justifyContent="flex-end">
              <Grid item xs={12} sm={6} md={3} lg={2}>
                <Button
                  color="color"
                  variant="contained"
                  label="TAMBAH"
                  size="large"
                  onClick={() => setIsShowModal(true)}
                  fullWidth={true}
                  py={1}
                />
              </Grid>
            </Grid>
            <TableContainer
              component={Paper}
              sx={{
                overflowX: 'auto',
                width: '100%',
              }}
            >
              <Table sx={{ minWidth: 750, border: '1px solid #ccc' }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell style={{ width: '5%' }}>#ID</TableCell>
                    <TableCell width={'20%'}>Hari</TableCell>
                    <TableCell width={'20%'}>Mulai</TableCell>
                    <TableCell width={'20%'}>Selesai</TableCell>
                    <TableCell width={'20%'}>Durasi Dibayar (jam)</TableCell>
                    <TableCell width={'15%'}>Aksi</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell colSpan={6}>
                        <LoadingComponent />
                      </TableCell>
                    </TableRow>
                  ) : data.length !== 0 ? (
                    <>
                      {data.map((item, index) => (
                        <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell>{item.id}</TableCell>
                          <TableCell>{DAY_LABEL[item.day] ?? item.day}</TableCell>
                          <TableCell>{formatTime(item.start_at)}</TableCell>
                          <TableCell>{formatTime(item.finish_at)}</TableCell>
                          <TableCell>{(item.payable_duration / 60).toFixed(2)}</TableCell>
                          <TableCell>
                            <Link
                              sx={{ cursor: 'pointer', mr: 2 }}
                              underline="hover"
                              onClick={() => handleEditModal(item.id)}
                            >
                              [Lihat]
                            </Link>
                            <Link
                              sx={{ cursor: 'pointer' }}
                              underline="hover"
                              onClick={() =>
                                setDeleteTarget({
                                  id: item.id,
                                  name: `${DAY_LABEL[item.day] ?? item.day} ${formatTime(item.start_at)}-${formatTime(
                                    item.finish_at
                                  )}`,
                                })
                              }
                            >
                              [Hapus]
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))}
                    </>
                  ) : (
                    <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell colSpan={6} sx={{ textAlign: 'center' }}>
                        Tidak ada data yang tersedia
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Grid
              item
              xs={12}
              sm={12}
              container
              justifyContent="flex-end"
              sx={{
                marginTop: 2,
              }}
            >
              <Stack direction="row" justifyContent="end">
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
              </Stack>
            </Grid>
          </Grid>
        </Grid>

        <ModalComponent open={isShowAddModal} close={() => setIsShowModal(false)} title={'Tambah Shift Lembur'}>
          <ModalAddOvertimeShift onClick={() => setIsShowModal(false)} onDataAdd={handleChildData} />
        </ModalComponent>
        <ModalComponent open={isEditModalShow} close={() => setIsEditModalShow(false)} title={'Edit Shift Lembur'}>
          <ModalEditOvertimeShift id={id} onClick={() => setIsEditModalShow(false)} onDataUpdate={handleChildData} />
        </ModalComponent>
        <ModalComponent open={deleteTarget !== null} close={() => setDeleteTarget(null)} title={'Hapus Shift Lembur'}>
          <ConfirmDeleteModal
            description={`Apakah Anda yakin ingin menghapus shift lembur "${deleteTarget?.name}"? Tindakan ini tidak dapat dibatalkan.`}
            onConfirm={() => handleDelete(deleteTarget.id)}
            onClick={() => setDeleteTarget(null)}
          />
        </ModalComponent>
      </Box>
    </>
  );
}
